import { StorageBufferAttribute } from 'three/webgpu';
import { assertStorageBufferBudget } from './bindingBudget';
import { JOINT_RECORD_VEC4S, jointRecordHelpers } from './jointRecord';
import { qrot, worldInvInertia } from './quatUtils';
import { SPRING_RECORD_VEC4S, springRecordHelpers } from './springRecord';
import { localId, storage, uniform, wgsl, wgslFn, workgroupId } from './tslCompat';

const WORKGROUP_SIZE = 64;
const BODY_COLOR_KERNEL_VARIANTS = 32;
const WORLD_BODY_INDEX = 0xffffffff;
const CONSTRAINT_REF_TAG_MASK = 0xc0000000;
const CONSTRAINT_REF_INDEX_MASK = 0x3fffffff;
const CONSTRAINT_REF_TAG_JOINT = 0x80000000;
const CONSTRAINT_REF_TAG_SPRING = 0xc0000000;
const BODY_COLOR_FALLBACK_FLAG = 0x01000000;

export type VelocityConstraintProjectionAllocationTelemetry = {
  disposed: boolean;
  fallbackVelocityState: {
    shared: true;
    owned: false;
    logicalVec4Count: number;
    cpuBytes: number;
    expectedGpuBytes: number;
  };
};

const velocityConstraintProjectionHelpers = wgsl(/* wgsl */`
      const VELOCITY_PROJECTION_WORLD_BODY_INDEX: u32 = 0xffffffffu;
      const VELOCITY_PROJECTION_JOINT_TYPE_FIXED: u32 = 1u;

      fn velocityProjectionIsFinite(value: f32) -> bool {
        return value == value && abs(value) <= 0x1.fffffep+127f;
      }

      fn velocityProjectionAxis(axisIndex: u32) -> vec3f {
        if (axisIndex == 0u) { return vec3f(1.0, 0.0, 0.0); }
        if (axisIndex == 1u) { return vec3f(0.0, 1.0, 0.0); }
        return vec3f(0.0, 0.0, 1.0);
      }
`);

/**
 * Velocity-level bilateral projection for AVBD hard joints and hard distance
 * constraints.
 *
 * The stage consumes the body constraint lists and colors already produced by
 * AVBD prepare(). Each color dispatch writes only the selected body's velocity;
 * adjacent constrained bodies therefore never race. Colors run in strict
 * sequence, giving an in-place block Gauss-Seidel sweep while retaining GPU
 * parallelism inside one color. Bodies that exhaust the configured colors run
 * afterwards through a read-old/write-scratch/commit Jacobi lane, so even
 * fallback-fallback joint and spring edges remain race-free.
 *
 * Finite-stiffness joint/spring rows are deliberately ignored. This stage is a
 * post-contact velocity projection, not a second finite-energy spring solve.
 */
export class VelocityConstraintProjectionStage {
  private readonly jointColorKernels: any[];
  private readonly springColorKernels: any[];
  private readonly fallbackProjectionKernel: any;
  private readonly fallbackCommitKernel: any;
  private readonly fallbackVelocityState: StorageBufferAttribute;
  private readonly maxBodies: number;
  private readonly fallbackVelocityVec4Count: number;
  private disposed = false;

  constructor(
    jointRecords: StorageBufferAttribute,
    springRecords: StorageBufferAttribute,
    positions: StorageBufferAttribute,
    quaternions: StorageBufferAttribute,
    velocities: StorageBufferAttribute,
    angularVelocities: StorageBufferAttribute,
    inverseInertia: StorageBufferAttribute,
    bodyConstraintCounts: StorageBufferAttribute,
    bodyConstraintRefs: StorageBufferAttribute,
    maxBodies: number,
    maxJoints: number,
    maxSprings: number,
    maxConstraintsPerBody: number,
    sharedVelocityScratch?: StorageBufferAttribute,
  ) {
    const boundedMaxBodies = Math.max(1, Math.floor(maxBodies));
    const boundedMaxJoints = Math.max(1, Math.floor(maxJoints));
    const boundedMaxSprings = Math.max(1, Math.floor(maxSprings));
    const boundedMaxConstraintsPerBody = Math.max(1, Math.floor(maxConstraintsPerBody));
    this.maxBodies = boundedMaxBodies;
    this.fallbackVelocityVec4Count = boundedMaxBodies * 2;
    // The fallback lane is Jacobi: every fallback body reads the velocity state
    // left by the strict color passes, writes only its own temporary result,
    // and commits in a following dispatch. Packing linear/angular state into a
    // single attribute keeps the fallback solver at the requested ten-storage
    // binding limit. The projection only runs in PhysicsEngine's restitution
    // branch, so reusing restitution's two-vec4-per-body scratch is race-free:
    // every projection fallback write/commit completes before the next
    // restitution gather overwrites that same storage.
    if (!sharedVelocityScratch) {
      throw new Error(
        'VelocityConstraintProjectionStage requires RestitutionStage shared velocity scratch.',
      );
    }
    if (
      sharedVelocityScratch.itemSize !== 4
      || sharedVelocityScratch.count < this.fallbackVelocityVec4Count
    ) {
      throw new Error(
        'VelocityConstraintProjectionStage shared velocity scratch has an invalid layout: '
        + `itemSize=${sharedVelocityScratch.itemSize}, count=${sharedVelocityScratch.count}, `
        + `requiredCount=${this.fallbackVelocityVec4Count}.`,
      );
    }
    this.fallbackVelocityState = sharedVelocityScratch;
    const constraintFixedWords = boundedMaxBodies * boundedMaxConstraintsPerBody;
    const constraintOverflowHeadOffset = constraintFixedWords + 1;
    const constraintOverflowNodeOffset = constraintOverflowHeadOffset + boundedMaxBodies;
    const constraintOverflowCapacity = Math.floor(
      (bodyConstraintRefs.count - constraintOverflowNodeOffset) / 2,
    );
    const constraintRefHelpers = wgsl(/* wgsl */`
      fn loadVelocityConstraintRef(
        refs: ptr<storage, array<u32>, read>,
        body: u32,
        logicalIndex: u32,
        overflowNode: ptr<function, u32>,
      ) -> u32 {
        if (logicalIndex < ${boundedMaxConstraintsPerBody}u) {
          return refs[body * ${boundedMaxConstraintsPerBody}u + logicalIndex];
        }
        let node = *overflowNode;
        if (node == 0xffffffffu || node >= ${constraintOverflowCapacity}u) {
          return 0x3fffffffu;
        }
        let nodeBase = ${constraintOverflowNodeOffset}u + node * 2u;
        *overflowNode = refs[nodeBase + 1u];
        return refs[nodeBase];
      }
    `);

    // Split joint and spring projection into separate kernels. Each shader then
    // stays at the WebGPU baseline limit of eight storage-buffer bindings.
    const jointProjectionShader = wgslFn(/* wgsl */`
      fn compute(
        jointRecords: ptr<storage, array<vec4f>, read_write>,
        positions: ptr<storage, array<vec4f>, read>,
        quaternions: ptr<storage, array<vec4f>, read>,
        velocities: ptr<storage, array<vec4f>, read_write>,
        angularVelocities: ptr<storage, array<vec4f>, read_write>,
        inverseInertia: ptr<storage, array<vec4f>, read>,
        bodyConstraintCounts: ptr<storage, array<u32>, read>,
        bodyConstraintRefs: ptr<storage, array<u32>, read>,
        bodyCount: u32,
        currentColor: u32,
        relaxation: f32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= bodyCount) { return; }

        let countWord = bodyConstraintCounts[gid];
        // Fallback bodies must not enter this in-place color projection: two
        // adjacent fallback endpoints may carry the same nominal color. They
        // are projected by the separate Jacobi solve/commit pair below.
        if ((countWord & ${BODY_COLOR_FALLBACK_FLAG}u) != 0u) { return; }
        let bodyColor = (countWord >> 16u) & 0xffu;
        if (bodyColor != currentColor) { return; }
        let constraintCount = countWord & 0xffffu;
        if (constraintCount == 0u) { return; }

        let invMass = max(positions[gid].w, 0.0);
        if (invMass <= 0.0) { return; }
        let selfQ = normalize(quaternions[gid]);
        let selfWorldInvInertia = worldInvInertia(
          selfQ,
          max(inverseInertia[gid].xyz, vec3f(0.0)),
        );
        var linearVelocity = velocities[gid].xyz;
        var angularVelocity = angularVelocities[gid].xyz;
        var constraintOverflowNode = bodyConstraintRefs[
          ${constraintOverflowHeadOffset}u + gid
        ];
        for (var slot = 0u; slot < constraintCount; slot++) {
          let constraintRef = loadVelocityConstraintRef(
            bodyConstraintRefs, gid, slot, &constraintOverflowNode,
          );
          let constraintTag = constraintRef & ${CONSTRAINT_REF_TAG_MASK}u;
          if (constraintTag != ${CONSTRAINT_REF_TAG_JOINT}u) { continue; }
          let jointIndex = constraintRef & ${CONSTRAINT_REF_INDEX_MASK}u;
          if (jointIndex >= ${boundedMaxJoints}u) { continue; }

          let jointMeta = loadJointMetaWords(jointRecords, jointIndex);
          if (jointMeta.w == 0u) { continue; }
          let bodyA = jointMeta.x;
          let bodyB = jointMeta.y;
          let isA = bodyA == gid;
          let isB = bodyB == gid;
          if (!isA && !isB) { continue; }
          if (bodyB >= bodyCount) { continue; }
          if (bodyA != VELOCITY_PROJECTION_WORLD_BODY_INDEX && bodyA >= bodyCount) { continue; }

          let stiffness = loadJointStiffness(jointRecords, jointIndex);
          let hardLinear = !velocityProjectionIsFinite(stiffness.x);
          let hardAngular = jointMeta.z == VELOCITY_PROJECTION_JOINT_TYPE_FIXED
            && !velocityProjectionIsFinite(stiffness.y);
          if (!hardLinear && !hardAngular) { continue; }

          let anchorA = loadJointAnchorA(jointRecords, jointIndex).xyz;
          let anchorB = loadJointAnchorB(jointRecords, jointIndex).xyz;
          var qA = vec4f(0.0, 0.0, 0.0, 1.0);
          var qB = normalize(quaternions[bodyB]);
          var rA = vec3f(0.0);
          var rB = qrot(qB, anchorB);
          var velocityA = vec3f(0.0);
          var velocityB = velocities[bodyB].xyz;
          var angularA = vec3f(0.0);
          var angularB = angularVelocities[bodyB].xyz;

          if (bodyA != VELOCITY_PROJECTION_WORLD_BODY_INDEX) {
            qA = normalize(quaternions[bodyA]);
            rA = qrot(qA, anchorA);
            velocityA = velocities[bodyA].xyz;
            angularA = angularVelocities[bodyA].xyz;
          }
          if (isA) {
            qA = selfQ;
            rA = qrot(qA, anchorA);
            velocityA = linearVelocity;
            angularA = angularVelocity;
          } else {
            qB = selfQ;
            rB = qrot(qB, anchorB);
            velocityB = linearVelocity;
            angularB = angularVelocity;
          }

          let selfSign = select(-1.0, 1.0, isA);
          let selfArm = select(rB, rA, isA);

          if (hardLinear) {
            // Project the three ball-socket rows in world coordinates. Updating
            // the local body state after each row is a small block GS solve and
            // naturally handles off-centre anchor angular coupling.
            for (var axisIndex = 0u; axisIndex < 3u; axisIndex++) {
              let axis = velocityProjectionAxis(axisIndex);
              if (isA) {
                velocityA = linearVelocity;
                angularA = angularVelocity;
              } else {
                velocityB = linearVelocity;
                angularB = angularVelocity;
              }
              let pointVelocityA = velocityA + cross(angularA, rA);
              let pointVelocityB = velocityB + cross(angularB, rB);
              let residual = dot(axis, pointVelocityA - pointVelocityB);
              let jLinear = selfSign * axis;
              let jAngular = selfSign * cross(selfArm, axis);
              let angularResponse = selfWorldInvInertia * jAngular;
              let effectiveMass = invMass + dot(jAngular, angularResponse);
              if (effectiveMass > 1e-12) {
                let deltaLambda = -relaxation * residual / effectiveMass;
                linearVelocity += invMass * jLinear * deltaLambda;
                angularVelocity += angularResponse * deltaLambda;
              }
            }
          }

          if (hardAngular) {
            // A fixed joint requires equal world angular velocities. The rest
            // orientation is already maintained by AVBD's position solve; the
            // velocity row therefore has a zero relative-angular target.
            for (var axisIndex = 0u; axisIndex < 3u; axisIndex++) {
              let axis = velocityProjectionAxis(axisIndex);
              if (isA) {
                angularA = angularVelocity;
              } else {
                angularB = angularVelocity;
              }
              let residual = dot(axis, angularA - angularB);
              let jAngular = selfSign * axis;
              let angularResponse = selfWorldInvInertia * jAngular;
              let effectiveMass = dot(jAngular, angularResponse);
              if (effectiveMass > 1e-12) {
                let deltaLambda = -relaxation * residual / effectiveMass;
                angularVelocity += angularResponse * deltaLambda;
              }
            }
          }
        }

        velocities[gid] = vec4f(linearVelocity, velocities[gid].w);
        angularVelocities[gid] = vec4f(angularVelocity, angularVelocities[gid].w);
      }
    `, [qrot, worldInvInertia, jointRecordHelpers, velocityConstraintProjectionHelpers, constraintRefHelpers]);

    const createJointColorKernel = (color: number): any => jointProjectionShader({
      jointRecords: storage(jointRecords, 'vec4f', boundedMaxJoints * JOINT_RECORD_VEC4S),
      positions: storage(positions, 'vec4f', boundedMaxBodies).toReadOnly(),
      quaternions: storage(quaternions, 'vec4f', boundedMaxBodies).toReadOnly(),
      velocities: storage(velocities, 'vec4f', boundedMaxBodies),
      angularVelocities: storage(angularVelocities, 'vec4f', boundedMaxBodies),
      inverseInertia: storage(inverseInertia, 'vec4f', boundedMaxBodies).toReadOnly(),
      bodyConstraintCounts: storage(bodyConstraintCounts, 'uint', boundedMaxBodies).toReadOnly(),
      bodyConstraintRefs: storage(
        bodyConstraintRefs,
        'uint',
        bodyConstraintRefs.count,
      ).toReadOnly(),
      bodyCount: uniform(0),
      currentColor: uniform(color),
      relaxation: uniform(1.0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName(`AVBD Velocity Joint Projection Color ${color}`);

    this.jointColorKernels = Array.from(
      { length: BODY_COLOR_KERNEL_VARIANTS },
      (_, color) => createJointColorKernel(color),
    );
    assertStorageBufferBudget('AVBD Velocity Joint Projection', 8);

    const springProjectionShader = wgslFn(/* wgsl */`
      fn compute(
        springRecords: ptr<storage, array<vec4f>, read_write>,
        positions: ptr<storage, array<vec4f>, read>,
        quaternions: ptr<storage, array<vec4f>, read>,
        velocities: ptr<storage, array<vec4f>, read_write>,
        angularVelocities: ptr<storage, array<vec4f>, read_write>,
        inverseInertia: ptr<storage, array<vec4f>, read>,
        bodyConstraintCounts: ptr<storage, array<u32>, read>,
        bodyConstraintRefs: ptr<storage, array<u32>, read>,
        bodyCount: u32,
        currentColor: u32,
        relaxation: f32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= bodyCount) { return; }

        let countWord = bodyConstraintCounts[gid];
        if ((countWord & ${BODY_COLOR_FALLBACK_FLAG}u) != 0u) { return; }
        let bodyColor = (countWord >> 16u) & 0xffu;
        if (bodyColor != currentColor) { return; }
        let constraintCount = countWord & 0xffffu;
        if (constraintCount == 0u) { return; }

        let invMass = max(positions[gid].w, 0.0);
        if (invMass <= 0.0) { return; }
        let selfQ = normalize(quaternions[gid]);
        let selfWorldInvInertia = worldInvInertia(
          selfQ,
          max(inverseInertia[gid].xyz, vec3f(0.0)),
        );
        var linearVelocity = velocities[gid].xyz;
        var angularVelocity = angularVelocities[gid].xyz;
        var constraintOverflowNode = bodyConstraintRefs[
          ${constraintOverflowHeadOffset}u + gid
        ];
        for (var slot = 0u; slot < constraintCount; slot++) {
          let constraintRef = loadVelocityConstraintRef(
            bodyConstraintRefs, gid, slot, &constraintOverflowNode,
          );
          let constraintTag = constraintRef & ${CONSTRAINT_REF_TAG_MASK}u;
          if (constraintTag != ${CONSTRAINT_REF_TAG_SPRING}u) { continue; }
          let springIndex = constraintRef & ${CONSTRAINT_REF_INDEX_MASK}u;
          if (springIndex >= ${boundedMaxSprings}u) { continue; }

          let springMeta = loadSpringMetaWords(springRecords, springIndex);
          if (springMeta.z == 0u || springMeta.w == 0u) { continue; }
          let bodyA = springMeta.x;
          let bodyB = springMeta.y;
          let isA = bodyA == gid;
          let isB = bodyB == gid;
          if (!isA && !isB) { continue; }
          if (bodyB >= bodyCount) { continue; }
          if (bodyA != VELOCITY_PROJECTION_WORLD_BODY_INDEX && bodyA >= bodyCount) { continue; }

          let anchorA = loadSpringAnchorARest(springRecords, springIndex).xyz;
          let anchorB = loadSpringAnchorBStiffness(springRecords, springIndex).xyz;
          var qA = vec4f(0.0, 0.0, 0.0, 1.0);
          var qB = normalize(quaternions[bodyB]);
          var positionA = anchorA;
          var positionB = positions[bodyB].xyz;
          var rA = vec3f(0.0);
          var rB = qrot(qB, anchorB);
          var velocityA = vec3f(0.0);
          var velocityB = velocities[bodyB].xyz;
          var angularA = vec3f(0.0);
          var angularB = angularVelocities[bodyB].xyz;

          if (bodyA != VELOCITY_PROJECTION_WORLD_BODY_INDEX) {
            qA = normalize(quaternions[bodyA]);
            positionA = positions[bodyA].xyz;
            rA = qrot(qA, anchorA);
            velocityA = velocities[bodyA].xyz;
            angularA = angularVelocities[bodyA].xyz;
          }
          if (isA) {
            qA = selfQ;
            rA = qrot(qA, anchorA);
            velocityA = linearVelocity;
            angularA = angularVelocity;
          } else {
            qB = selfQ;
            rB = qrot(qB, anchorB);
            velocityB = linearVelocity;
            angularB = angularVelocity;
          }

          let worldAnchorA = select(positionA + rA, anchorA, bodyA == VELOCITY_PROJECTION_WORLD_BODY_INDEX);
          let worldAnchorB = positionB + rB;
          let delta = worldAnchorA - worldAnchorB;
          let length2 = dot(delta, delta);
          if (length2 <= 1e-12) { continue; }
          let normal = delta * inverseSqrt(length2);
          let pointVelocityA = velocityA + cross(angularA, rA);
          let pointVelocityB = velocityB + cross(angularB, rB);
          let residual = dot(normal, pointVelocityA - pointVelocityB);
          let selfSign = select(-1.0, 1.0, isA);
          let selfArm = select(rB, rA, isA);
          let jLinear = selfSign * normal;
          let jAngular = selfSign * cross(selfArm, normal);
          let angularResponse = selfWorldInvInertia * jAngular;
          let effectiveMass = invMass + dot(jAngular, angularResponse);
          if (effectiveMass > 1e-12) {
            let deltaLambda = -relaxation * residual / effectiveMass;
            linearVelocity += invMass * jLinear * deltaLambda;
            angularVelocity += angularResponse * deltaLambda;
          }
        }

        velocities[gid] = vec4f(linearVelocity, velocities[gid].w);
        angularVelocities[gid] = vec4f(angularVelocity, angularVelocities[gid].w);
      }
    `, [qrot, worldInvInertia, springRecordHelpers, velocityConstraintProjectionHelpers, constraintRefHelpers]);

    const createSpringColorKernel = (color: number): any => springProjectionShader({
      springRecords: storage(springRecords, 'vec4f', boundedMaxSprings * SPRING_RECORD_VEC4S),
      positions: storage(positions, 'vec4f', boundedMaxBodies).toReadOnly(),
      quaternions: storage(quaternions, 'vec4f', boundedMaxBodies).toReadOnly(),
      velocities: storage(velocities, 'vec4f', boundedMaxBodies),
      angularVelocities: storage(angularVelocities, 'vec4f', boundedMaxBodies),
      inverseInertia: storage(inverseInertia, 'vec4f', boundedMaxBodies).toReadOnly(),
      bodyConstraintCounts: storage(bodyConstraintCounts, 'uint', boundedMaxBodies).toReadOnly(),
      bodyConstraintRefs: storage(
        bodyConstraintRefs,
        'uint',
        bodyConstraintRefs.count,
      ).toReadOnly(),
      bodyCount: uniform(0),
      currentColor: uniform(color),
      relaxation: uniform(1.0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName(`AVBD Velocity Distance Projection Color ${color}`);

    this.springColorKernels = Array.from(
      { length: BODY_COLOR_KERNEL_VARIANTS },
      (_, color) => createSpringColorKernel(color),
    );
    assertStorageBufferBudget('AVBD Velocity Distance Projection', 8);

    // Bodies that could not obtain a strict graph color may be adjacent to one
    // another. Their velocity projection therefore cannot update the shared
    // velocity buffers in place. This kernel performs the same body-local
    // joint/spring block solve as the colored path, but reads every neighbour
    // from the immutable pre-fallback velocity state and writes only a private
    // two-vec4 result. A following dispatch commits all fallback bodies at
    // once, making fallback-fallback edges a true Jacobi lane while retaining
    // Gauss-Seidel ordering from the normal colors into the fallback bucket.
    const fallbackProjectionShader = wgslFn(/* wgsl */`
      fn compute(
        jointRecords: ptr<storage, array<vec4f>, read_write>,
        springRecords: ptr<storage, array<vec4f>, read_write>,
        positions: ptr<storage, array<vec4f>, read>,
        quaternions: ptr<storage, array<vec4f>, read>,
        velocities: ptr<storage, array<vec4f>, read>,
        angularVelocities: ptr<storage, array<vec4f>, read>,
        inverseInertia: ptr<storage, array<vec4f>, read>,
        bodyConstraintCounts: ptr<storage, array<u32>, read>,
        bodyConstraintRefs: ptr<storage, array<u32>, read>,
        fallbackVelocityState: ptr<storage, array<vec4f>, read_write>,
        bodyCount: u32,
        relaxation: f32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= bodyCount) { return; }

        let countWord = bodyConstraintCounts[gid];
        if ((countWord & ${BODY_COLOR_FALLBACK_FLAG}u) == 0u) { return; }
        let outputBase = gid * 2u;
        let constraintCount = countWord & 0xffffu;
        if (constraintCount == 0u || positions[gid].w <= 0.0) {
          fallbackVelocityState[outputBase] = velocities[gid];
          fallbackVelocityState[outputBase + 1u] = angularVelocities[gid];
          return;
        }

        let invMass = max(positions[gid].w, 0.0);
        let selfQ = normalize(quaternions[gid]);
        let selfWorldInvInertia = worldInvInertia(
          selfQ,
          max(inverseInertia[gid].xyz, vec3f(0.0)),
        );
        var linearVelocity = velocities[gid].xyz;
        var angularVelocity = angularVelocities[gid].xyz;
        var constraintOverflowNode = bodyConstraintRefs[
          ${constraintOverflowHeadOffset}u + gid
        ];

        for (var slot = 0u; slot < constraintCount; slot++) {
          let constraintRef = loadVelocityConstraintRef(
            bodyConstraintRefs, gid, slot, &constraintOverflowNode,
          );
          let constraintTag = constraintRef & ${CONSTRAINT_REF_TAG_MASK}u;
          let constraintIndex = constraintRef & ${CONSTRAINT_REF_INDEX_MASK}u;

          if (constraintTag == ${CONSTRAINT_REF_TAG_JOINT}u) {
            let jointIndex = constraintIndex;
            if (jointIndex >= ${boundedMaxJoints}u) { continue; }

            let jointMeta = loadJointMetaWords(jointRecords, jointIndex);
            if (jointMeta.w == 0u) { continue; }
            let bodyA = jointMeta.x;
            let bodyB = jointMeta.y;
            let isA = bodyA == gid;
            let isB = bodyB == gid;
            if (!isA && !isB) { continue; }
            if (bodyB >= bodyCount) { continue; }
            if (bodyA != VELOCITY_PROJECTION_WORLD_BODY_INDEX && bodyA >= bodyCount) {
              continue;
            }

            let stiffness = loadJointStiffness(jointRecords, jointIndex);
            let hardLinear = !velocityProjectionIsFinite(stiffness.x);
            let hardAngular = jointMeta.z == VELOCITY_PROJECTION_JOINT_TYPE_FIXED
              && !velocityProjectionIsFinite(stiffness.y);
            if (!hardLinear && !hardAngular) { continue; }

            let anchorA = loadJointAnchorA(jointRecords, jointIndex).xyz;
            let anchorB = loadJointAnchorB(jointRecords, jointIndex).xyz;
            var qA = vec4f(0.0, 0.0, 0.0, 1.0);
            var qB = normalize(quaternions[bodyB]);
            var rA = vec3f(0.0);
            var rB = qrot(qB, anchorB);
            var velocityA = vec3f(0.0);
            var velocityB = velocities[bodyB].xyz;
            var angularA = vec3f(0.0);
            var angularB = angularVelocities[bodyB].xyz;

            if (bodyA != VELOCITY_PROJECTION_WORLD_BODY_INDEX) {
              qA = normalize(quaternions[bodyA]);
              rA = qrot(qA, anchorA);
              velocityA = velocities[bodyA].xyz;
              angularA = angularVelocities[bodyA].xyz;
            }
            if (isA) {
              qA = selfQ;
              rA = qrot(qA, anchorA);
              velocityA = linearVelocity;
              angularA = angularVelocity;
            } else {
              qB = selfQ;
              rB = qrot(qB, anchorB);
              velocityB = linearVelocity;
              angularB = angularVelocity;
            }

            let selfSign = select(-1.0, 1.0, isA);
            let selfArm = select(rB, rA, isA);
            if (hardLinear) {
              for (var axisIndex = 0u; axisIndex < 3u; axisIndex++) {
                let axis = velocityProjectionAxis(axisIndex);
                if (isA) {
                  velocityA = linearVelocity;
                  angularA = angularVelocity;
                } else {
                  velocityB = linearVelocity;
                  angularB = angularVelocity;
                }
                let pointVelocityA = velocityA + cross(angularA, rA);
                let pointVelocityB = velocityB + cross(angularB, rB);
                let residual = dot(axis, pointVelocityA - pointVelocityB);
                let jLinear = selfSign * axis;
                let jAngular = selfSign * cross(selfArm, axis);
                let angularResponse = selfWorldInvInertia * jAngular;
                let effectiveMass = invMass + dot(jAngular, angularResponse);
                if (effectiveMass > 1e-12) {
                  let deltaLambda = -relaxation * residual / effectiveMass;
                  linearVelocity += invMass * jLinear * deltaLambda;
                  angularVelocity += angularResponse * deltaLambda;
                }
              }
            }

            if (hardAngular) {
              for (var axisIndex = 0u; axisIndex < 3u; axisIndex++) {
                let axis = velocityProjectionAxis(axisIndex);
                if (isA) {
                  angularA = angularVelocity;
                } else {
                  angularB = angularVelocity;
                }
                let residual = dot(axis, angularA - angularB);
                let jAngular = selfSign * axis;
                let angularResponse = selfWorldInvInertia * jAngular;
                let effectiveMass = dot(jAngular, angularResponse);
                if (effectiveMass > 1e-12) {
                  let deltaLambda = -relaxation * residual / effectiveMass;
                  angularVelocity += angularResponse * deltaLambda;
                }
              }
            }
            continue;
          }

          if (constraintTag == ${CONSTRAINT_REF_TAG_SPRING}u) {
            let springIndex = constraintIndex;
            if (springIndex >= ${boundedMaxSprings}u) { continue; }

            let springMeta = loadSpringMetaWords(springRecords, springIndex);
            if (springMeta.z == 0u || springMeta.w == 0u) { continue; }
            let bodyA = springMeta.x;
            let bodyB = springMeta.y;
            let isA = bodyA == gid;
            let isB = bodyB == gid;
            if (!isA && !isB) { continue; }
            if (bodyB >= bodyCount) { continue; }
            if (bodyA != VELOCITY_PROJECTION_WORLD_BODY_INDEX && bodyA >= bodyCount) {
              continue;
            }

            let anchorA = loadSpringAnchorARest(springRecords, springIndex).xyz;
            let anchorB = loadSpringAnchorBStiffness(springRecords, springIndex).xyz;
            var qA = vec4f(0.0, 0.0, 0.0, 1.0);
            var qB = normalize(quaternions[bodyB]);
            var positionA = anchorA;
            var positionB = positions[bodyB].xyz;
            var rA = vec3f(0.0);
            var rB = qrot(qB, anchorB);
            var velocityA = vec3f(0.0);
            var velocityB = velocities[bodyB].xyz;
            var angularA = vec3f(0.0);
            var angularB = angularVelocities[bodyB].xyz;

            if (bodyA != VELOCITY_PROJECTION_WORLD_BODY_INDEX) {
              qA = normalize(quaternions[bodyA]);
              positionA = positions[bodyA].xyz;
              rA = qrot(qA, anchorA);
              velocityA = velocities[bodyA].xyz;
              angularA = angularVelocities[bodyA].xyz;
            }
            if (isA) {
              qA = selfQ;
              rA = qrot(qA, anchorA);
              velocityA = linearVelocity;
              angularA = angularVelocity;
            } else {
              qB = selfQ;
              rB = qrot(qB, anchorB);
              velocityB = linearVelocity;
              angularB = angularVelocity;
            }

            let worldAnchorA = select(
              positionA + rA,
              anchorA,
              bodyA == VELOCITY_PROJECTION_WORLD_BODY_INDEX,
            );
            let worldAnchorB = positionB + rB;
            let delta = worldAnchorA - worldAnchorB;
            let length2 = dot(delta, delta);
            if (length2 <= 1e-12) { continue; }
            let normal = delta * inverseSqrt(length2);
            let pointVelocityA = velocityA + cross(angularA, rA);
            let pointVelocityB = velocityB + cross(angularB, rB);
            let residual = dot(normal, pointVelocityA - pointVelocityB);
            let selfSign = select(-1.0, 1.0, isA);
            let selfArm = select(rB, rA, isA);
            let jLinear = selfSign * normal;
            let jAngular = selfSign * cross(selfArm, normal);
            let angularResponse = selfWorldInvInertia * jAngular;
            let effectiveMass = invMass + dot(jAngular, angularResponse);
            if (effectiveMass > 1e-12) {
              let deltaLambda = -relaxation * residual / effectiveMass;
              linearVelocity += invMass * jLinear * deltaLambda;
              angularVelocity += angularResponse * deltaLambda;
            }
          }
        }

        fallbackVelocityState[outputBase] = vec4f(
          linearVelocity,
          velocities[gid].w,
        );
        fallbackVelocityState[outputBase + 1u] = vec4f(
          angularVelocity,
          angularVelocities[gid].w,
        );
      }
    `, [
      qrot,
      worldInvInertia,
      jointRecordHelpers,
      springRecordHelpers,
      velocityConstraintProjectionHelpers,
      constraintRefHelpers,
    ]);

    this.fallbackProjectionKernel = fallbackProjectionShader({
      jointRecords: storage(jointRecords, 'vec4f', boundedMaxJoints * JOINT_RECORD_VEC4S),
      springRecords: storage(springRecords, 'vec4f', boundedMaxSprings * SPRING_RECORD_VEC4S),
      positions: storage(positions, 'vec4f', boundedMaxBodies).toReadOnly(),
      quaternions: storage(quaternions, 'vec4f', boundedMaxBodies).toReadOnly(),
      velocities: storage(velocities, 'vec4f', boundedMaxBodies).toReadOnly(),
      angularVelocities: storage(angularVelocities, 'vec4f', boundedMaxBodies).toReadOnly(),
      inverseInertia: storage(inverseInertia, 'vec4f', boundedMaxBodies).toReadOnly(),
      bodyConstraintCounts: storage(bodyConstraintCounts, 'uint', boundedMaxBodies).toReadOnly(),
      bodyConstraintRefs: storage(
        bodyConstraintRefs,
        'uint',
        bodyConstraintRefs.count,
      ).toReadOnly(),
      fallbackVelocityState: storage(
        this.fallbackVelocityState,
        'vec4f',
        boundedMaxBodies * 2,
      ),
      bodyCount: uniform(0),
      relaxation: uniform(1.0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Velocity Fallback Jacobi Projection');
    assertStorageBufferBudget('AVBD Velocity Fallback Jacobi Projection', 10);

    const fallbackCommitShader = wgslFn(/* wgsl */`
      fn compute(
        velocities: ptr<storage, array<vec4f>, read_write>,
        angularVelocities: ptr<storage, array<vec4f>, read_write>,
        bodyConstraintCounts: ptr<storage, array<u32>, read>,
        fallbackVelocityState: ptr<storage, array<vec4f>, read>,
        bodyCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= bodyCount) { return; }
        if ((bodyConstraintCounts[gid] & ${BODY_COLOR_FALLBACK_FLAG}u) == 0u) {
          return;
        }
        let outputBase = gid * 2u;
        velocities[gid] = fallbackVelocityState[outputBase];
        angularVelocities[gid] = fallbackVelocityState[outputBase + 1u];
      }
    `);

    this.fallbackCommitKernel = fallbackCommitShader({
      velocities: storage(velocities, 'vec4f', boundedMaxBodies),
      angularVelocities: storage(angularVelocities, 'vec4f', boundedMaxBodies),
      bodyConstraintCounts: storage(bodyConstraintCounts, 'uint', boundedMaxBodies).toReadOnly(),
      fallbackVelocityState: storage(
        this.fallbackVelocityState,
        'vec4f',
        boundedMaxBodies * 2,
      ).toReadOnly(),
      bodyCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Velocity Fallback Jacobi Commit');
    assertStorageBufferBudget('AVBD Velocity Fallback Jacobi Commit', 4);
  }

  getAllocationTelemetry(): VelocityConstraintProjectionAllocationTelemetry {
    return {
      disposed: this.disposed,
      fallbackVelocityState: {
        shared: true,
        owned: false,
        logicalVec4Count: this.fallbackVelocityVec4Count,
        cpuBytes: this.fallbackVelocityState.array.byteLength,
        expectedGpuBytes: this.fallbackVelocityVec4Count * 4 * 4,
      },
    };
  }

  /**
   * This stage owns no raw GPU allocation. Its fallback storage is borrowed
   * from RestitutionStage and must be disposed by that owner only.
   */
  dispose(): void {
    this.disposed = true;
  }

  /**
   * Runs one or more strict color-ordered block Gauss-Seidel sweeps.
   *
   * Call after contact restitution and before the next integration substep.
   * bodyConstraintCounts/bodyConstraintRefs must still contain the lists and
   * colors produced by AVBD prepare() for the current substep.
   */
  dispatch(
    renderer: any,
    bodyCount: number,
    bodyColorCount: number,
    iterations = 2,
    relaxation = 1.0,
  ): void {
    if (this.disposed) return;
    const boundedBodyCount = Math.min(
      this.maxBodies,
      Math.max(0, Math.floor(bodyCount)),
    );
    const boundedColorCount = Math.max(
      0,
      Math.min(BODY_COLOR_KERNEL_VARIANTS, Math.floor(bodyColorCount)),
    );
    const boundedIterations = Number.isFinite(iterations)
      ? Math.max(0, Math.min(64, Math.floor(iterations)))
      : 0;
    const boundedRelaxation = Number.isFinite(relaxation)
      ? Math.max(0.0, Math.min(1.0, relaxation))
      : 1.0;

    if (
      boundedBodyCount === 0
      || boundedColorCount === 0
      || boundedIterations === 0
      || boundedRelaxation === 0.0
    ) {
      return;
    }

    const workgroups = Math.ceil(boundedBodyCount / WORKGROUP_SIZE);
    this.fallbackProjectionKernel.computeNode.parameters.bodyCount.value = boundedBodyCount;
    this.fallbackProjectionKernel.computeNode.parameters.relaxation.value = boundedRelaxation;
    this.fallbackCommitKernel.computeNode.parameters.bodyCount.value = boundedBodyCount;
    const pass: any[] = [];
    for (let iteration = 0; iteration < boundedIterations; iteration++) {
      for (let color = 0; color < boundedColorCount; color++) {
        const jointKernel = this.jointColorKernels[color]!;
        const springKernel = this.springColorKernels[color]!;
        jointKernel.computeNode.parameters.bodyCount.value = boundedBodyCount;
        jointKernel.computeNode.parameters.relaxation.value = boundedRelaxation;
        springKernel.computeNode.parameters.bodyCount.value = boundedBodyCount;
        springKernel.computeNode.parameters.relaxation.value = boundedRelaxation;
        pass.push(jointKernel, springKernel);
      }
      // All strict colors have committed before this pair runs. The solve
      // writes only private fallback output; the following dispatch is the
      // storage barrier and simultaneous Jacobi commit.
      pass.push(this.fallbackProjectionKernel, this.fallbackCommitKernel);
    }

    // Distinct kernel variants own distinct currentColor uniform buffers. One
    // compute pass therefore preserves color/iteration order and storage
    // visibility without one queue submission per color.
    renderer.compute(pass, [workgroups, 1, 1]);
  }
}

export const VELOCITY_CONSTRAINT_PROJECTION_WORLD_BODY_INDEX = WORLD_BODY_INDEX;
